// tslint:disable:no-string-literal
import * as React from 'react';
import gql from 'graphql-tag';
import { Button, Divider, Modal, Table } from 'antd';
import * as uuid from 'uuid';

import StateContainer from '../../StateContainer';
import Editor from './Editor';
import { ApolloClientFactory } from '../../../../../domain/apolloClientFactory';
import PricesDisplay from '../../../../../components/Others/PricesDisplay';
import Availabilities from 'src/components/Query/Availabilities';

export interface IProductCombinationsTableProps {
  currency: GQL.ICurrency;
  productAttributes: GQL.IAttribute[];
  // seznam úplně všech atributů z DB
  allAttributes: Array<GQL.IAttribute>;
  product: GQL.IProduct;
  variants: GQL.IProductVariant[];
  onChange: (variants: Array<GQL.IProductVariant>) => void;
  container: StateContainer;
}

interface IProductCombinationsTableState {
  variant: GQL.IProductVariant | undefined;
  editMode: boolean;
  errors: { [id: string]: string };
  availabilities: { [key: string]: string };
}

const ensureEanAndSku = gql`
  query($ean: String!) {
    isVariantValid(ean: $ean)
  }
`;

export default class ProductCombinationsTable extends React.Component<IProductCombinationsTableProps,
  IProductCombinationsTableState> {

  constructor(props: IProductCombinationsTableProps) {
    super(props);

    this.renderActions = this.renderActions.bind(this);
    this.dismissEditing = this.dismissEditing.bind(this);
    this.pushOrUpdateVariant = this.pushOrUpdateVariant.bind(this);

    this.state = {
      variant: undefined,
      editMode: false,
      errors: {},
      availabilities: {},
    };
  }

  componentDidMount() {
    if (this.props.product && Array.isArray(this.props.product.variants)) {
      this.setState({ availabilities: this.props.product.variants.reduce((av, pv) => {
        if (pv.availability) {
          av[pv.availability.id] = pv.availability.name;
        }
        return av;
      }, {})})
    }
  }

  onChangeVariant(variant) {
    this.setState({ variant });
  }

  public render() {
    const { currency } = this.props;

    return (
      <div>
        <Editor
          attributes={this.getAttributesWithMaxOneVisual()}
          allAttributes={this.props.allAttributes}
          variant={this.state.variant}
          dismissable={this.state.variant != undefined}
          editMode={this.state.editMode}
          onDismiss={this.dismissEditing}
          onChange={variant => this.onChangeVariant(variant)}
          onSave={this.pushOrUpdateVariant}
          errors={this.state.errors}
        />
        <Divider/>
        <Table
          pagination={false}
          dataSource={this.props.variants}
          rowKey={(value: GQL.IProductVariant) => {
            if (value.id != undefined) {
              return value.id;
            }

            return (value as any)._id;
          }}
        >
          {this.getAttributesWithMaxOneVisual().map((attribute: GQL.IAttribute) => (
            <Table.Column
              key={attribute.id}
              title={attribute.title}
              dataIndex={`definition.${attribute.id}`}
              render={(id: string, variant: GQL.IProductVariant) => { // SV: nerozumím tomu, jak se původně mělo dostat id vlastnosti do funkce render
                // return this.translateAttributeValueIndex(attribute, id); // SV: původní řešení s id, o kterém nevím, jak to mělo fungovat
                // Following code shows attributes as stored in database, so it does not reflect current changes
                // const attributeIDs:Array<string> = [];
                // if (variant.attributeValues && Array.isArray(variant.attributeValues)) {
                //  variant.attributeValues.forEach((av) => {
                //    if (av.id && attributeIDs) {
                //      attributeIDs.push(av.id);
                //    }
                //  });
                // }
                // return this.translateAttributeValueIndex(attribute, attributeIDs);

                if (variant.definition && attribute.id && variant.definition[attribute.id]) {
                  return this.translateAttributeValueIndex(attribute, variant.definition[attribute.id]);
                }
                return '——';
              }}
            />
          ))}
          <Table.Column
            key={'sku'}
            title={'Item number'}
            dataIndex={'sku'}
          />
          <Table.Column
            key={'ean'}
            title={'EAN'}
            dataIndex={'ean'}
          />
          <Table.Column
            key={'availability'}
            title={'Availability'}
            dataIndex={'availability'}
            render={(u, variant: GQL.IProductVariant) => {
              if (! variant) {
                return null;
              }
              if (! variant.availability) {
                return null;
              }
              return variant.availability.name;
            }}
          />
          <Table.Column
            key={'recommendedPrice'}
            title={'Recomm.'}
            dataIndex={'recommendedPrice'}
            width={170}
            align={'right'}
            render={(u, variant: GQL.IProductVariant) => {
              if (!currency) {
                return null;
              }

              return (
                <PricesDisplay
                  type={'recommendedPriceWithout'}
                  prices={variant.prices}
                  currency={currency}
                />
              );
            }}
          />
          <Table.Column
            key={'price'}
            title={'Price'}
            dataIndex={'price'}
            width={170}
            align={'right'}
            render={(u, variant: GQL.IProductVariant) => {
              if (!currency) {
                return null;
              }

              return (
                <PricesDisplay
                  type={'priceWithout'}
                  prices={variant.prices}
                  currency={currency}
                />
              );
            }}
          />
          <Table.Column
            key={'actions'}
            title={'Actions'}
            width={240}
            align={'center'}
            render={this.renderActions}
          />
        </Table>
      </div>
    );
  }

  public getAttributesWithMaxOneVisual() {
    const usedVisualAttribute = this.getUsedVisualAttribute();
    return this.props.productAttributes.filter(a => {
      if (a.isVisual && usedVisualAttribute != undefined) {
        // tslint:disable-next-line:triple-equals
        return a.id == usedVisualAttribute.id;
      }

      return true;
    });
  }

  public getUsedVisualAttribute() {
    return this.props.container.getUsedVisualAttribute();
  }

  private async validate(variant: GQL.IProductVariant | undefined) {
    const message = 'This field is required';
    const errors = {};
    const { variants } = this.props;
    const previousVariant =
      variant != undefined &&
      (variant.id != undefined || variant['_id'] != undefined )
        ? variants.find(v => v.id == variant.id) : undefined;

    const usedVisualAttribute = this.getUsedVisualAttribute();
    if (variant == undefined) {
      errors[ 'ean' ] = message;

      if (usedVisualAttribute) {
        errors[ usedVisualAttribute.id ] = message;
      }

      this.setState({ errors });
      return false;
    }

    if (usedVisualAttribute) {
      const val = variant.definition[ usedVisualAttribute.id ];
      if (val == undefined) {
        errors[ usedVisualAttribute.id ] = message;
      }
    }

    if (!variant.ean) {
      if (!variant.ean) {
        errors[ 'ean' ] = message;
      }
    } else {
      if (
        previousVariant != undefined && previousVariant.ean == variant.ean
      ) {
        // do nothin
      } else {
        const alreadyInTable = variants.some(v => v.ean == variant.ean);
        if (alreadyInTable) {
          if (variants.some(v => v.ean == variant.ean)) {
            errors[ 'ean' ] = 'Combination taken';
          } else if (variants.some(v => v.ean == variant.ean)) {
            errors[ 'ean' ] = 'Already taken';
          }
        } else {
          const response = await ApolloClientFactory.GetClient().query({
            query: ensureEanAndSku,
            variables: {
              ean: variant.ean,
            }
          });

          const valid = (response as any).data.isVariantValid;

          switch (valid) {
            case 1:
              errors[ 'ean' ] = 'Already taken';
              break;
            default:
              console.log('this variant is okay', valid);
          }
        }
      }
    }

    this.setState({ errors });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    return true;
  }

  private renderActions(variant: GQL.IProductVariant, index: number) {
    return (
      <Button.Group>
        <Button onClick={() => this.onClone(variant)}>Clone</Button>
        <Button onClick={() => this.onEdit(variant)}>Edit</Button>
        <Button danger={true} onClick={() => this.deleteVariant(variant)}>Delete</Button>
      </Button.Group>
    );
  }

  /**
   * Vytvoření klonu varianty, vč. atributů
   * - odstraní se EAN
   *
   * @param {GQL.IProductVariant} variant
   * @returns {Promise<void>}
   */
  private async onClone(variant: GQL.IProductVariant) {
    const clone = {...variant};
    clone.ean = '';
    delete clone.id;

    this.setState({ variant: clone, editMode: false, errors: {} });
  }

  private onEdit(variant: GQL.IProductVariant) {
    this.setState({ variant, editMode: true, errors: {} });
  }

  private dismissEditing() {
    this.setState({ variant: undefined, editMode: false, errors: {} });
  }

  private deleteVariant(variant: GQL.IProductVariant) {
    Modal.confirm({
      title: `Delete variant?`,
      content: <span>Are you sure you want to delete variant?</span>,
      onOk: () => {
        const variants = this.props.variants.filter(v => {
          if (v.id != undefined && variant.id != undefined) {
            return v.id.toString() !== variant.id.toString();
          } else if ((v as any)._id != undefined && (variant as any)._id != undefined) {
            return (v as any)._id !== (variant as any)._id;
          }

          return true;
        });

        this.props.onChange(variants);
        this.dismissEditing();
      }
    });
  }

  private translateAttributeValueIndex(attribute: GQL.IAttribute, valueId: string | string[]) {
    if (!valueId) {
      return '—';
    }

    if (Array.isArray(valueId)) {
      let finalString = '';
      for (const id of valueId) {
        const attrValue = attribute.values && attribute.values.find(v => v.id.toString() === id.toString());
        if (attrValue) {
          if (finalString == '') {
            finalString += attrValue.title;
          } else {
            finalString += ', ' + attrValue.title;
          }
        }
      }

      return finalString;
    }

    const attrValue = attribute.values && attribute.values.find(v => v.id.toString() === valueId.toString());

    return attrValue ? attrValue.title : valueId;
  }

  /**
   * Vykreslení ceny v tabulce
   *
   * @param {string} value
   * @returns {string}
   */
  private displayPrice(value: string) {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    return value;
  }

  private async pushOrUpdateVariant() {
    if (!(await this.validate(this.state.variant))) {
      return;
    }

    if (this.state.variant) {
      const variant = { ...this.state.variant };
      if (variant.id == undefined && (variant as any)._id == undefined) {
        (variant as any)._id = uuid.v4();
      }

      if (variant.definition) {
        for (const key of Object.keys(variant.definition)) {
          if (Array.isArray(variant.definition[ key ])) {
            variant.definition[ key ] = variant.definition[ key ].filter(v => v != undefined);
          }
        }
      }

      if (!variant.sku) {
        variant.sku = this.props.product && this.props.product.sku && this.props.product.sku.toString() || '';
      }

      // setting variant.availability to show correct availability in table
      if ('undefined' === typeof variant.availabilityId) {
        variant.availability = null;
      } else if ('string' === typeof variant.availabilityId) {
        if ((! variant.availability) || (variant.availability && (variant.availability.id !== variant.availabilityId))) {
          if ('undefined' === typeof this.state.availabilities[variant.availabilityId]) {
            const loadAvailability = gql`
              query($id: String!) {
                availabilityById(id: $id) {
                  id
                  name
                }
              }
            `;
            const response = await ApolloClientFactory.GetClient().query({
              query: loadAvailability,
              variables: {
                id: variant.availabilityId,
              }
            });
  
            const availability = (response as any).data.availabilityById;
            if (availability.id && availability.name) {
              this.setState({ availabilities: { ...this.state.availabilities, [variant.availabilityId]: availability.name}});
            }
            variant.availability = availability;
          } else {
            variant.availability = {
              id: variant.availabilityId,
              name: this.state.availabilities[variant.availabilityId],
            } as any;
          }
        }
      }

      if (this.state.editMode && variant) {
        const variantIndex = this.props.variants.findIndex(v => {
          if (v.id != undefined && variant.id != undefined) {
            return v.id.toString() === variant.id.toString();
          } else if ((v as any)._id != undefined && (variant as any)._id != undefined) {
            return (v as any)._id === (variant as any)._id;
          }

          return false;
        });

        const variants = [ ...this.props.variants ];
        variants[ variantIndex ] = variant;

        this.props.onChange(variants);
      } else if (variant) {
        this.props.onChange([ ...this.props.variants, variant ]);
      }

      this.dismissEditing();
    }
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Products/ProductDetail/ProductVariantsTab/ProductCombinationsTable/ProductCombinationsTable.tsx